/**
 * Free-preview limit — shared by every player surface (videoPage, webinar,
 * live, livePage) so a non-subscriber gets the same deal everywhere: two
 * minutes of playback, then a paywall.
 *
 * A component using this mixin must provide:
 *   - `is_user_can_watch` (computed): true for subscribers, who skip the limit.
 *   - `previewKey`        (computed): stable id for the thing being watched,
 *                                     e.g. 'item_12'. Empty string = not ready.
 *   - `this.player`       (data):     the video.js instance.
 *
 * and must call:
 *   - `restorePreviewState()` once `previewKey` resolves (after the API load),
 *   - `initPreviewGuard()`    once the player exists (playerCreated / onReady),
 *   - `trackPreview(currentTime)` from the player's timeupdate.
 *
 * Template hooks: `previewBlocked` (show the paywall) and `previewGateOpen`
 * (render the player at all).
 */

export const PREVIEW_LIMIT_SECONDS = 120;

// sessionStorage, not localStorage: the allowance resets when the browser
// session ends instead of paywalling the same title forever.
const STORAGE_PREFIX = 'preview_used_';

export default {
  data() {
    return {
      previewSecondsWatched: 0,
      previewLastTime: null,
      previewExceeded: false,
      PREVIEW_LIMIT_SECONDS,
    };
  },

  computed: {
    /** Paywall is up: allowance spent and the viewer is not a subscriber. */
    previewBlocked() {
      return this.previewExceeded && !this.is_user_can_watch;
    },
    /** Player should render: subscriber, or preview still has time left. */
    previewGateOpen() {
      return this.is_user_can_watch || !this.previewExceeded;
    },
    /**
     * On-demand video: the preview is the first two minutes OF THE VIDEO, so
     * seeking past 0:02:00 ends it. Live surfaces override this to false —
     * their playhead is elapsed broadcast time (often hours), so position says
     * nothing about how much the viewer has watched.
     */
    previewUsesPlayhead() {
      return true;
    },
  },

  watch: {
    previewExceeded(newVal) {
      if (newVal) this.enforcePreviewBlock();
      else this.releasePreviewBlock();
    },
    is_user_can_watch(newVal) {
      // Subscribed mid-session — drop the block and the spent-allowance flag.
      if (newVal === true) this.resetPreview();
    },
  },

  methods: {
    _previewStorageKey() {
      const key = this.previewKey;
      return key ? STORAGE_PREFIX + key : '';
    },

    /** Re-apply a spent allowance from earlier in this browser session. */
    restorePreviewState() {
      const key = this._previewStorageKey();
      if (!key || this.is_user_can_watch) return;
      if (sessionStorage.getItem(key) === '1') this.previewExceeded = true;
    },

    /**
     * Two ways to spend the allowance:
     *   - playhead past the limit (on-demand only) — seeking to 0:05:00 ends
     *     the preview, it does not hand out the rest of the film,
     *   - two minutes actually played, so pause/seek-back cannot farm more.
     */
    trackPreview(currentTime) {
      if (this.is_user_can_watch || this.previewExceeded) return;

      if (this.previewLastTime !== null) {
        const delta = currentTime - this.previewLastTime;
        if (delta > 0 && delta < 2) this.previewSecondsWatched += delta;
      }
      this.previewLastTime = currentTime;

      const pastLimit = this.previewUsesPlayhead && currentTime >= this.PREVIEW_LIMIT_SECONDS;
      if (pastLimit || this.previewSecondsWatched >= this.PREVIEW_LIMIT_SECONDS) {
        this.previewExceeded = true;
        const key = this._previewStorageKey();
        if (key) sessionStorage.setItem(key, '1');
        this.enforcePreviewBlock();
      }
    },

    /**
     * Wall-clock fallback for surfaces with no video.js instance to listen to
     * — live streams delivered as a third-party iframe embed. Nothing can be
     * paused there, so the block is the template dropping the embed once
     * `previewGateOpen` goes false.
     */
    startPreviewClock() {
      if (this.is_user_can_watch || this.previewExceeded || this._previewClock) return;
      this._previewClock = setInterval(() => {
        this.previewSecondsWatched += 1;
        if (this.previewSecondsWatched >= this.PREVIEW_LIMIT_SECONDS) {
          this.previewExceeded = true;
          const key = this._previewStorageKey();
          if (key) sessionStorage.setItem(key, '1');
          this.stopPreviewClock();
          this.enforcePreviewBlock();
        }
      }, 1000);
    },

    stopPreviewClock() {
      if (!this._previewClock) return;
      clearInterval(this._previewClock);
      this._previewClock = null;
    },

    /** Re-pause on every play attempt once the allowance is spent. */
    initPreviewGuard() {
      if (!this.player) return;
      this.player.on('play', () => this.enforcePreviewBlock());
      // The flag may already be set from an earlier view in this session.
      this.enforcePreviewBlock();
    },

    /**
     * Pausing once is not a block: the paywall is a page-level fixed overlay,
     * so in fullscreen it is not even visible, and space / click / PiP resume
     * playback. Leave fullscreen, pause, and take the controls away.
     */
    enforcePreviewBlock() {
      if (!this.previewBlocked || !this.player) return;
      try {
        if (this.player.isFullscreen()) this.player.exitFullscreen();
      } catch (e) { /* noop */ }
      try { this.player.pause(); } catch (e) { /* noop */ }
      try { this.player.controls(false); } catch (e) { /* noop */ }
    },

    releasePreviewBlock() {
      if (!this.player) return;
      try { this.player.controls(true); } catch (e) { /* noop */ }
    },

    resetPreview() {
      this.previewExceeded = false;
      this.previewSecondsWatched = 0;
      this.previewLastTime = null;
      const key = this._previewStorageKey();
      if (key) {
        sessionStorage.removeItem(key);
        // Legacy permanent flag from the localStorage version of this feature.
        localStorage.removeItem(key);
      }
      this.releasePreviewBlock();
    },
  },

  beforeDestroy() {
    this.stopPreviewClock();
  },
};
